import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { optimizeImage } from "@/lib/mediaUrls";

/**
 * Phase 9.7.16 — 🏆 Most Pushed Today
 *
 * Top 5 artists ranked by **24h boosted-reach growth** (UTC day).
 * Powered by GET /api/leaderboard/most-pushed-today.
 *
 * Visual: dense vertical list (so the rail can sit alongside other
 * Discover sections without consuming a full horizontal row).
 */

const fmt = (n) => {
  const v = Math.round(Number(n) || 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return String(v);
};

const RankMedal = ({ rank }) => {
  const map = {
    1: { emoji: "🥇", color: "from-amber-400 to-yellow-500" },
    2: { emoji: "🥈", color: "from-slate-300 to-slate-500" },
    3: { emoji: "🥉", color: "from-orange-400 to-amber-600" },
  };
  const meta = map[rank];
  if (meta) {
    return (
      <div
        className={`w-9 h-9 rounded-full bg-gradient-to-br ${meta.color} flex items-center justify-center text-lg shadow-lg shadow-amber-500/20 flex-shrink-0`}
        data-testid={`pushed-rank-${rank}`}
      >
        {meta.emoji}
      </div>
    );
  }
  return (
    <div
      className="w-9 h-9 rounded-full bg-gray-800/80 border border-gray-700 flex items-center justify-center text-sm font-bold text-gray-300 flex-shrink-0"
      data-testid={`pushed-rank-${rank}`}
    >
      #{rank}
    </div>
  );
};

const MostPushedToday = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const run = async () => {
      try {
        const res = await api.get("/leaderboard/most-pushed-today");
        setData(res.data);
      } catch (_e) {
        setData({ leaderboard: [] });
      } finally {
        setLoading(false);
      }
    };
    run();
    // Refresh every 60s — matches "today" semantics + light on the API
    const id = setInterval(run, 60_000);
    return () => clearInterval(id);
  }, []);

  if (loading) return null;
  if (!data || !data.leaderboard || data.leaderboard.length === 0) return null;

  return (
    <section
      className="my-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
      data-testid="section-most-pushed-today"
    >
      <div className="flex items-end justify-between mb-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
            <span>🏆</span>
            <span>Most Pushed Today</span>
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Top artists by boosted reach growth in the last 24 hours
          </p>
        </div>
        <span className="hidden sm:block text-xs px-2 py-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300">
          Live · refreshes every minute
        </span>
      </div>

      <div
        className="rounded-2xl bg-gradient-to-br from-amber-900/10 via-black to-purple-900/10 border border-amber-500/20 p-3 sm:p-4 divide-y divide-gray-800/60"
        data-testid="most-pushed-today-list"
      >
        {data.leaderboard.map((row) => (
          <Link
            key={row.user_id}
            to={`/artist/${row.user_id}`}
            className="flex items-center gap-3 py-3 first:pt-1 last:pb-1 hover:bg-white/[0.02] transition-colors rounded-lg px-2 -mx-2"
            data-testid={`most-pushed-row-${row.rank}`}
          >
            <RankMedal rank={row.rank} />

            {/* Avatar */}
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-fuchsia-600 to-purple-600 flex-shrink-0 overflow-hidden ring-2 ring-amber-500/20">
              {row.artist_photo ? (
                <img
                  src={optimizeImage(row.artist_photo, 'avatar')}
                  alt={row.artist_name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white font-bold text-sm">
                  {(row.artist_name || "?").charAt(0).toUpperCase()}
                </div>
              )}
            </div>

            {/* Artist + top track */}
            <div className="flex-1 min-w-0">
              <p
                className="text-white font-semibold text-sm truncate"
                data-testid={`pushed-artist-name-${row.rank}`}
              >
                {row.artist_name}
              </p>
              {row.top_track && (
                <p className="text-[11px] text-gray-400 truncate">
                  🎵 {row.top_track.track_title}
                  {row.track_count > 1 && (
                    <span className="text-gray-500">
                      {" "}
                      · +{row.track_count - 1} more
                    </span>
                  )}
                </p>
              )}
            </div>

            {/* Delta */}
            <div className="text-right flex-shrink-0">
              <p
                className="text-amber-300 font-bold text-base sm:text-lg leading-none"
                data-testid={`pushed-delta-${row.rank}`}
              >
                +{fmt(row.delta_24h)}
              </p>
              <p className="text-[10px] uppercase tracking-wider text-amber-500/70 mt-1">
                reach 24h
              </p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default MostPushedToday;
